/**
 * THE DECISION FOR THE "Your current setup" CARD, AND NOTHING ELSE.
 *
 * TEMPORARY BY DESIGN. Joe's **AI-1257** ships the real resolver and REPLACES
 * this one module. That is the whole reason the decision lives here rather than
 * in the card: `CurrentSetupCard` reads the `SetupCardState` union and nothing
 * else — no slice, no query, no provider list, no counts of its own — so
 * swapping this file for AI-1257's leaves the card, its copy and its tests
 * untouched.
 *
 * NO THIRD-PARTY STATE, on purpose. The card exists only while Thanx is
 * collecting, and `owningThirdPartyProvider` in `../feedbackOwnership`
 * ("Thanx collection wins the overlap") never lets a connected tool own
 * feedback while Thanx collects, so the connected tool is not an input at all. If that rule ever
 * lets a third party win while Thanx collects, this union needs a state for it
 * again.
 *
 * If you are tempted to add a branch to the card's JSX, add a union member
 * here instead.
 */

export type SetupCardState =
  // The card is NOT RENDERED AT ALL — not hidden, not empty. Written feedback
  // is off, so there is no setup to describe.
  | { kind: 'not_collecting' }
  // Thanx is collecting and RecoveryAI is not answering.
  | { kind: 'thanx' }
  // RecoveryAI answers at SOME locations. `activeLocations` < `totalLocations`;
  // the card derives the remainder, it is never carried here pre-computed.
  | { kind: 'rai_pilot'; activeLocations: number; totalLocations: number }
  // RecoveryAI answers everywhere.
  | { kind: 'rai_all_locations' }

// N and M, as `recovery/activities/metrics` reports them. `null` covers every
// way that endpoint can fail to answer — a 403 from `require_recovery_ai!`, a
// 403 from the `merchant_settings` view permission, a skipped query, a response
// that omits `active_locations_count`, or a request still in flight. The
// resolver treats all of them the same: RecoveryAI is not answering, pick a
// non-RecoveryAI state, and render.
export type RecoveryAiCoverage = {
  activeLocations: number
  totalLocations: number
}

export type SetupCardInputs = {
  // `allow_written_feedback_collection`. Off means no card.
  writtenFeedbackEnabled: boolean
  recoveryAi: RecoveryAiCoverage | null
}

// RecoveryAI only counts as answering when it is live at a real location. A
// zero (or absent) denominator means the metrics endpoint has nothing to say
// about coverage, so the card must not render "0 of 0".
// A type guard, not a boolean: it narrows `recoveryAi` for the block below, so
// the counts are read without an `as` assertion.
const isRecoveryAiAnswering = (
  coverage: RecoveryAiCoverage | null
): coverage is RecoveryAiCoverage =>
  coverage !== null &&
  coverage.totalLocations > 0 &&
  coverage.activeLocations > 0

export const resolveSetupCardState = ({
  writtenFeedbackEnabled,
  recoveryAi,
}: SetupCardInputs): SetupCardState => {
  if (!writtenFeedbackEnabled) return { kind: 'not_collecting' }

  if (!isRecoveryAiAnswering(recoveryAi)) return { kind: 'thanx' }

  const { activeLocations, totalLocations } = recoveryAi

  // `>=`, not `===`: an active count that overshoots the visible-location
  // denominator still means full coverage, not a pilot with a negative
  // remainder.
  if (activeLocations >= totalLocations) return { kind: 'rai_all_locations' }

  return { kind: 'rai_pilot', activeLocations, totalLocations }
}
